import { formatInTimeZone } from "date-fns-tz";

import type { HosClockKind, HosRecapSummary } from "@/types/driverHos";

/**
 * What each clock is called on the block, by the hours each one allows, as drivers and dispatchers name them.
 *
 * The 8-hour clock is **8H Rest**: the driving it allows before the 30-minute break is due, not the break's length.
 * `70H Cycle` assumes the 70-hour week; neither hos-clocks nor the forecast carries a driver's cycle cap.
 */
export const HOS_CLOCK_LABELS: Record<HosClockKind, string> = {
  break: "8H Rest",
  drive: "11H Drive",
  shift: "14H Shift",
  cycle: "70H Cycle",
};

/** Time left on a clock as `h:mm`, rounded down so a clock never reads as having more than it does. */
export function formatHosRemaining(secondsLeft: number): string {
  const minutes = Math.floor(Math.abs(secondsLeft) / 60);
  const sign = secondsLeft < 0 ? "-" : "";
  return `${sign}${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, "0")}`;
}

/** Hours coming back as `+8h 15m`. */
function formatGain(gainedSeconds: number): string {
  const minutes = Math.floor(gainedSeconds / 60);
  return `+${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, "0")}m`;
}

/**
 * The weekday of a bare `YYYY-MM-DD`, read at UTC midnight so the dispatcher's own zone cannot move it a day.
 */
function weekdayOf(date: string): string {
  return formatInTimeZone(new Date(`${date}T00:00:00Z`), "UTC", "EEE");
}

/** The recap as one line: `Recap · +8h 15m Fri · +11h 00m Sat`; null when the timer carries none. */
export function formatRecap(recap: readonly HosRecapSummary[]): string | null {
  if (recap.length === 0) return null;
  return ["Recap", ...recap.map((day) => `${formatGain(day.gainedSeconds)} ${weekdayOf(day.date)}`)].join(" · ");
}
